<template>
	<!-- Flat List View (Compact) -->
	<v-list-item
		v-if="flat"
		data-test="document-file"
		:title="documentName({ id: item.id, file: item })"
	>
		<template #prepend>
			<v-avatar
				v-if="avatar"
				:icon="getIconAndColor(item).icon"
				:color="getIconAndColor(item).color"
				variant="tonal"
				size="x-small"
			>
			</v-avatar>
		</template>

		<v-list-item-subtitle
			class="d-flex align-center flex-wrap"
			style="gap: 6px"
		>
			<v-chip
				v-if="item?.document_type?.name"
				density="compact"
				size="small"
				variant="tonal"
				color="primary"
			>
				{{ item.document_type.display_label || item.document_type.name }}
			</v-chip>
			<v-chip
				v-if="item?.file_size"
				density="compact"
				size="small"
				color="info"
			>
				{{ getFormatBytes(item.file_size) }}
			</v-chip>
			<v-chip
				v-if="item?.created"
				density="compact"
				size="small"
				color="success"
			>
				{{ timeAgo(item.created) }}
			</v-chip>
		</v-list-item-subtitle>

		<template v-if="link || action || preview || canDelete" #append>
			<v-list-item-action class="d-flex flex-row align-center" style="gap: 4px">
				<DialogPreview
					v-if="preview"
					:id="item.id"
					:handle-prev-route="false"
					:dialog="false"
				>
					<template #activator="{ props }">
						<v-btn
							v-bind="props"
							variant="text"
							size="x-small"
							icon="far fa-eye"
							@click.stop
						/>
					</template>
				</DialogPreview>
				<v-btn
					v-if="link"
					:to="typeof linkTo === 'function' ? linkTo(item) : linkTo"
					target="_blank"
					variant="text"
					size="x-small"
					icon="fa-solid fa-up-right-from-square"
					@click.stop
				/>
				<v-btn
					v-if="action"
					color="primary"
					variant="text"
					size="x-small"
					icon="fa fa-download"
					@click.stop="saveFile(item, downloadEndpoint)"
				/>
				<v-btn
					v-if="canDelete"
					data-test="delete-btn"
					color="error"
					variant="text"
					size="x-small"
					icon="fa fa-trash"
					:title="t('common.delete.name')"
					@click.stop="emit('delete', item)"
				/>
			</v-list-item-action>
		</template>
	</v-list-item>

	<!-- Grouped View (Detailed) -->
	<v-list-item
		v-else
		data-test="document-file"
		:title="documentName({ id: item.id, file: item })"
	>
		<template #prepend>
			<v-avatar
				v-if="avatar"
				:icon="getIconAndColor(item).icon"
				:color="getIconAndColor(item).color"
				variant="tonal"
				size="x-small"
			>
			</v-avatar>
		</template>
		<v-list-item-subtitle>
			<v-chip v-if="item?.file_size" density="compact" color="info">
				{{ getFormatBytes(item.file_size) }}
			</v-chip>
			<v-chip
				v-if="item?.created"
				density="compact"
				color="success"
				prepend-icon="far fa-plus"
			>
				{{ timeAgo(item.created) }}
			</v-chip>
		</v-list-item-subtitle>

		<template v-if="link || action || preview || canDelete" #append>
			<v-list-item-action
				class="d-flex flex-column justify-center align-center"
			>
				<DialogPreview
					v-if="preview"
					:id="item.id"
					:handle-prev-route="false"
					:dialog="false"
				>
					<template #activator="{ props }">
						<v-btn
							v-bind="props"
							variant="text"
							size="small"
							icon="far fa-eye"
							@click.stop
						/>
					</template>
				</DialogPreview>
				<v-btn
					v-if="link"
					:to="typeof linkTo === 'function' ? linkTo(item) : linkTo"
					target="_blank"
					variant="text"
					size="small"
					icon="fa-solid fa-up-right-from-square"
					@click.stop
				/>

				<v-btn
					v-if="action"
					color="primary"
					variant="tonal"
					size="small"
					@click.stop="saveFile(item, downloadEndpoint)"
				>
					<v-icon size="small" class="mr-1" icon="fa fa-download"></v-icon>
					<span v-if="!dense" class="hidden-md-and-down"> Download </span>
				</v-btn>
				<v-btn
					v-if="canDelete"
					data-test="delete-btn"
					color="error"
					variant="text"
					size="small"
					icon="fa fa-trash"
					:title="t('common.delete.name')"
					@click.stop="emit('delete', item)"
				/>
			</v-list-item-action>
		</template>
	</v-list-item>
</template>

<script setup lang="ts">
import DialogPreview from '@/components/modules/Documents/DialogPreview.vue'
import { useDayJs } from '@/composables/dateTime'
import { getFormatBytes } from '@/helpers/FormatValue'
import { useDocumentStore } from '@/stores/modules/document'
import { useI18n } from 'vue-i18n'
import type { RouteLocation } from 'vue-router'

const {
	item,
	linkTo = (item) => ({
		name: 'document',
		params: { id: item.id },
	}),
} = defineProps<{
	link?: boolean
	dense?: boolean
	action?: boolean
	preview?: boolean
	flat?: boolean
	avatar?: boolean
	/**
	 * Opt-in: blendet den Löschen-Button ein. Standard aus, damit die übrigen
	 * Verwendungen der Liste (Notizen, FAQ, Belegprüfung, E-Mail, verknüpfte
	 * Elemente) unverändert bleiben.
	 */
	canDelete?: boolean
	item: Api.Document
	downloadEndpoint?: string
	linkTo?: RouteLocation | ((item: Api.Document) => RouteLocation)
}>()
const emit = defineEmits<{ delete: [item: Api.Document] }>()
const { t } = useI18n()
const { documentName, saveFile } = useDocumentStore()
const { timeAgo } = useDayJs()

function getIconAndColor(file: Api.Document) {
	const fileName = file.file_original_name || file.file_notes || ''
	const extension = fileName.split('.').pop()?.toLowerCase() ?? 'default'
	return {
		icon: t(`file.${extension}.icon`),
		color: t(`file.${extension}.color`),
	}
}
</script>
<style scoped>
/* Vollständige Anzeige des Titels ohne Truncation */
:deep(.v-list-item-title) {
	white-space: normal !important;
	word-break: break-word;
}
</style>
